import {
  Company,
  DisplayUserType,
  FormattedDisplayUserOptions,
  Role,
  User,
} from '@adsk/alloy-provider';

import {
  ASSIGNEE_PICKER_COMPANY,
  ASSIGNEE_PICKER_ROLE,
  ASSIGNEE_PICKER_USER,
} from '../consts';
import { useTranslation } from '../i18n';
import {
  AdditionalEntityDataFields,
  Status,
  EntityOptionsFilter,
  ItemWithGroup,
  StringValueKeys,
} from '../types';
import { getSubTitleString, IdToNameMap } from './utils';

export const transformRoles = (
  roles: Role[],
  valueGetter: StringValueKeys<Role>,
  t: ReturnType<typeof useTranslation>['t'],
  statusFilters?: Status[],
  roleOptionsFilter?: EntityOptionsFilter<Role>,
  additionalDataFields?: AdditionalEntityDataFields<Role>,
  rolesMemberCount: Record<string, number> = {},
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
  hideInactiveEntitiesInMenu?: boolean,
) => {
  // Pre-compile additional data fields logic
  const hasAdditionalFields =
    additionalDataFields && additionalDataFields.length > 0;

  return postTransform(
    roles.reduce<ItemWithGroup[]>((result, role, i) => {
      const isStatusVisible =
        !statusFilters || statusFilters.includes(role.status);

      const isOptionVisible =
        !roleOptionsFilter ||
        roleOptionsFilter(role, i, roles, rolesMemberCount);

      if (isStatusVisible && isOptionVisible) {
        // Build item object without spreads for better performance
        const item: ItemWithGroup = {
          label: role.name,
          value: role[valueGetter],
          subtext: t('MEMBERS_COUNT', {
            count: rolesMemberCount[role.id] || 0,
          }),
          group: ASSIGNEE_PICKER_ROLE,
          memberCount: rolesMemberCount[role.id],
          hidden: hideInactiveEntitiesInMenu
            ? role.status === 'inactive'
            : undefined,
        };

        // Add additional fields if they exist - avoiding reduce and spread
        if (hasAdditionalFields) {
          for (let k = 0; k < additionalDataFields!.length; k++) {
            const field = additionalDataFields![k];
            (item as Record<string, unknown>)[field] = role[field];
          }
        }

        result.push(item);
      }

      return result;
    }, []),
  );
};

export const transformUsers = (
  users: User[],
  valueGetter: StringValueKeys<User>,
  formatDisplayUser: (
    userId: string,
    options?: FormattedDisplayUserOptions | undefined,
  ) => DisplayUserType,
  t: ReturnType<typeof useTranslation>['t'],
  statusFilters?: Status[],
  userOptionsFilter?: EntityOptionsFilter<User>,
  additionalDataFields?: AdditionalEntityDataFields<User>,
  companyIdToNameMap: IdToNameMap = {},
  roles: Role[] = [],
  userOptionSubtextRenderer?: (user: User) => string,
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
  hideDeletedEntitiesInMenu?: boolean,
): [Record<string, number>, ItemWithGroup[]] => {
  // Pre-calculate roleIdToNameMap without object spread
  const roleIdToNameMap: { [id: string]: string } = {};
  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    roleIdToNameMap[role.id] = role.name;
  }

  // Pre-calculate rolesCountMap to avoid doing it in the main loop
  const rolesCountMap: { [id: string]: number } = {};
  for (let i = 0; i < users.length; i++) {
    const user = users[i];
    if (user.roleIds) {
      for (let j = 0; j < user.roleIds.length; j++) {
        const roleId = user.roleIds[j];
        rolesCountMap[roleId] = (rolesCountMap[roleId] || 0) + 1;
      }
    }
  }

  // Pre-compile additional data fields logic
  const hasAdditionalFields =
    additionalDataFields && additionalDataFields.length > 0;

  const options = postTransform(
    users.reduce<ItemWithGroup[]>((result, user, i) => {
      const isStatusVisible =
        !statusFilters || statusFilters.includes(user.status);

      const isOptionVisible =
        !userOptionsFilter || userOptionsFilter(user, i, users);

      if (isStatusVisible && isOptionVisible && user.autodeskId) {
        // Build item object without spreads for better performance
        const item: ItemWithGroup = {
          label:
            formatDisplayUser(user.autodeskId).displayName ||
            user.name ||
            user.email,
          value: user[valueGetter],
          subtext: userOptionSubtextRenderer
            ? userOptionSubtextRenderer(user)
            : getSubTitleString(user, companyIdToNameMap, roleIdToNameMap, t),
          group: ASSIGNEE_PICKER_USER,
          imageUrl: user?.imageUrl,
          hidden: hideDeletedEntitiesInMenu
            ? user.status === 'deleted'
            : undefined,
        };

        // Add additional fields if they exist - avoiding reduce and spread
        if (hasAdditionalFields) {
          for (let k = 0; k < additionalDataFields!.length; k++) {
            const field = additionalDataFields![k];
            (item as Record<string, unknown>)[field] = user[field];
          }
        }

        result.push(item);
      }

      return result;
    }, []),
  );

  return [rolesCountMap, options];
};

export const transformCompanies = (
  companies: Company[],
  valueGetter: StringValueKeys<Company>,
  t: ReturnType<typeof useTranslation>['t'],
  statusFilters?: Status[],
  companyOptionsFilter?: EntityOptionsFilter<Company>,
  additionalDataFields?: AdditionalEntityDataFields<Company>,
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
): [Record<string, string>, ItemWithGroup[]] => {
  // Pre-compile additional data fields logic
  const hasAdditionalFields =
    additionalDataFields && additionalDataFields.length > 0;

  const idToNameMap: { [id: string]: string } = {};
  const options = postTransform(
    companies.reduce<ItemWithGroup[]>((result, company, i) => {
      const isStatusVisible =
        !statusFilters ||
        (company.status && statusFilters.includes(company.status));

      const isOptionVisible =
        !companyOptionsFilter || companyOptionsFilter(company, i, companies);

      if (isStatusVisible) {
        idToNameMap[company.id] = company.name;

        if (isOptionVisible) {
          // Build item object without spreads for better performance
          const item: ItemWithGroup = {
            label: company.name,
            value: company[valueGetter],
            group: ASSIGNEE_PICKER_COMPANY,
            subtext: t('MEMBERS_COUNT', {
              count: company?.memberCount || 0,
            }),
            imageUrl: company?.imageUrl,
            memberCount: company.memberCount,
          };

          // Add additional fields if they exist - avoiding reduce and spread
          if (hasAdditionalFields) {
            for (let k = 0; k < additionalDataFields!.length; k++) {
              const field = additionalDataFields![k];
              (item as Record<string, unknown>)[field] = company[field];
            }
          }

          result.push(item);
        }
      }

      return result;
    }, []),
  );

  return [idToNameMap, options];
};
